import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import { SupplierProfile } from '../../lib/api';
import api from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface RiskMatrixModalProps {
  open: boolean;
  onClose: () => void;
  supplier: SupplierProfile | null;
}

export default function RiskMatrixModal({ open, onClose, supplier }: RiskMatrixModalProps) {
  const [loading, setLoading] = useState(false);
  const { formatDate } = useFormatters();
  const [matrixData, setMatrixData] = useState<{
    overall_risk_level: string;
    overall_risk_score: number;
    assessment_date: string;
    matrix_data: Array<{ category: string; category_type: string; risk_level: string; risk_score: number; likelihood: number; consequence: number; weight?: number; notes?: string }>;
  } | null>(null);

  const supplierCompanyId = supplier?.company;
  useEffect(() => {
    if (!open || !supplierCompanyId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/suppliers/risk-assessments/risk_matrix_data/?supplier=${supplierCompanyId}`)
      .then((response) => { if (!cancelled) setMatrixData(response.data); })
      .catch((error: unknown) => {
        console.error('Error fetching risk data:', error);
        if (!cancelled) setMatrixData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, supplierCompanyId]);

  const getRiskColor = (level: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-500 text-white',
      medium: 'bg-yellow-500 text-white',
      high: 'bg-orange-500 text-white',
      critical: 'bg-red-500 text-white',
    };
    return colors[level] || 'bg-gray-500 text-white';
  };

  const getRiskLevelColor = (level: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getLikelihoodLabel = (value: number) => {
    const labels: Record<number, string> = {
      5: 'Almost Certain',
      4: 'Likely',
      3: 'Possible',
      2: 'Unlikely',
      1: 'Rare',
    };
    return labels[value] || '';
  };

  const getConsequenceLabel = (value: number) => {
    const labels: Record<number, string> = {
      5: 'Catastrophic',
      4: 'Major',
      3: 'Moderate',
      2: 'Minor',
      1: 'Insignificant',
    };
    return labels[value] || '';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Risk Assessment Matrix - {supplier?.company_name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : matrixData ? (
          <div className="space-y-6">
            {/* Overall Risk */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-accent/50">
              <div>
                <p className="text-sm text-muted-foreground">Overall Risk Level</p>
                <Badge className={`${getRiskLevelColor(matrixData.overall_risk_level)} text-lg px-3 py-1 mt-1`}>
                  {matrixData.overall_risk_level.toUpperCase()}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Risk Score</p>
                <p className="text-3xl font-bold">{matrixData.overall_risk_score.toFixed(1)}</p>
                <p className="text-xs text-muted-foreground">out of 100</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Assessment Date</p>
                <p className="font-semibold">{formatDate(matrixData.assessment_date)}</p>
              </div>
            </div>

            {/* Risk Matrix */}
            <div>
              <h3 className="font-semibold mb-4">Risk Matrix (Likelihood × Consequence)</h3>
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full">
                  <div className="grid grid-cols-6 gap-2">
                    {/* Header Row */}
                    <div className="col-span-1"></div>
                    <div className="text-center text-xs font-medium p-2">Insignificant<br/>(1)</div>
                    <div className="text-center text-xs font-medium p-2">Minor<br/>(2)</div>
                    <div className="text-center text-xs font-medium p-2">Moderate<br/>(3)</div>
                    <div className="text-center text-xs font-medium p-2">Major<br/>(4)</div>
                    <div className="text-center text-xs font-medium p-2">Catastrophic<br/>(5)</div>

                    {/* Likelihood rows */}
                    {[5, 4, 3, 2, 1].map((likelihood) => (
                      <>
                        <div className="flex items-center justify-end text-xs font-medium pr-2">
                          <div className="text-right">
                            {getLikelihoodLabel(likelihood)}<br/>
                            <span className="text-muted-foreground">({likelihood})</span>
                          </div>
                        </div>
                        {[1, 2, 3, 4, 5].map((consequence) => {
                          const score = likelihood * consequence;
                          const riskLevel = score >= 20 ? 'critical' : score >= 12 ? 'high' : score >= 6 ? 'medium' : 'low';
                          return (
                            <div
                              key={`${likelihood}-${consequence}`}
                              className={`${getRiskColor(riskLevel)} h-16 rounded flex items-center justify-center font-bold text-lg cursor-pointer hover:opacity-80 transition-opacity`}
                              title={`${getLikelihoodLabel(likelihood)} × ${getConsequenceLabel(consequence)} = ${score}`}
                            >
                              {score}
                            </div>
                          );
                        })}
                      </>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span>Low (1-5)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span>Medium (6-11)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span>High (12-19)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span>Critical (20-25)</span>
                </div>
              </div>
            </div>

            {/* Category Risks */}
            {matrixData.matrix_data && matrixData.matrix_data.length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">Risk Categories Breakdown</h3>
                <div className="space-y-2">
                  {matrixData.matrix_data.map((risk: { category: string; category_type: string; risk_level: string; risk_score: number; likelihood: number; consequence: number; weight?: number; notes?: string }, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className="flex-1">
                        <p className="font-medium">{risk.category}</p>
                        <p className="text-xs text-muted-foreground capitalize">{risk.category_type.replace('_', ' ')}</p>
                      </div>
                      <div className="text-center mx-4">
                        <p className="text-xs text-muted-foreground">Likelihood</p>
                        <p className="font-semibold">{risk.likelihood}</p>
                      </div>
                      <div className="text-center mx-4">
                        <p className="text-xs text-muted-foreground">Consequence</p>
                        <p className="font-semibold">{risk.consequence}</p>
                      </div>
                      <div className="text-center mx-4">
                        <p className="text-xs text-muted-foreground">Score</p>
                        <p className="font-bold text-lg">{risk.risk_score.toFixed(1)}</p>
                      </div>
                      <Badge className={getRiskLevelColor(risk.risk_level)}>
                        {risk.risk_level.toUpperCase()}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                <Loader2 className="h-8 w-8 text-muted-foreground" />
              </div>
            </div>
            <p className="text-muted-foreground mb-2">No risk assessment found for this supplier.</p>
            <p className="text-sm text-muted-foreground mb-4">
              Create a risk assessment to evaluate supplier risk across multiple categories.
            </p>
            <Button onClick={() => alert('Risk assessment creation feature coming soon! This will open a form to create a comprehensive supplier risk assessment.')}>
              Create Risk Assessment
            </Button>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
